import { AlertTriangle, CalendarDays, Check, Clock3, MapPin, MessageSquare, Trash2, User, X } from 'lucide-react';
import { RiderRideRequest } from '@/types';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface RiderRideRequestCardProps {
  request: RiderRideRequest;
  driverView?: boolean;
  isResponding?: boolean;
  onAccept?: () => void;
  onReject?: () => void;
  onDelete?: () => void;
  onMessageRider?: () => void;
}

const openMap = (address: string) => window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`, '_blank', 'noopener,noreferrer');

export const RiderRideRequestCard = ({ request, driverView = false, isResponding, onAccept, onReject, onDelete, onMessageRider }: RiderRideRequestCardProps) => (
  <article className={cn('overflow-hidden rounded-2xl border bg-card shadow-sm', request.isEmergency ? 'border-red-300 dark:border-red-900' : 'border-border/70')}>
    <div className={cn('h-1.5', request.isEmergency ? 'bg-red-500' : 'bg-gradient-to-r from-primary to-accent')} />
    <div className="space-y-4 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <div className={cn('rounded-xl p-2.5', request.isEmergency ? 'bg-red-100 text-red-600 dark:bg-red-950' : 'bg-primary/10 text-primary')}>
            {request.isEmergency ? <AlertTriangle className="h-5 w-5" /> : <User className="h-5 w-5" />}
          </div>
          <div className="min-w-0">
            <p className="font-semibold">{driverView ? request.rider.name : 'Your ride request'}</p>
            <p className={cn('text-xs font-medium', request.isEmergency ? 'text-red-600' : 'text-muted-foreground')}>{request.isEmergency ? 'Emergency request' : 'Standard request'}</p>
          </div>
        </div>
        <span className={cn('rounded-full px-2.5 py-1 text-xs font-semibold capitalize', request.status === 'accepted' ? 'bg-green-100 text-green-700' : request.status === 'open' ? 'bg-amber-100 text-amber-700' : 'bg-muted text-muted-foreground')}>{request.status}</span>
      </div>

      <div className="rounded-xl bg-muted/40 p-3 text-sm">
        <button type="button" onClick={() => openMap(request.pickupLocation)} className="flex max-w-full items-center gap-2 hover:text-primary hover:underline">
          <span className="h-2 w-2 shrink-0 rounded-full bg-primary" />
          <span className="truncate">{request.pickupLocation}</span>
        </button>
        <div className="ml-1 h-5 border-l border-dashed border-border" />
        <button type="button" onClick={() => openMap(request.destinationName)} className="flex max-w-full items-center gap-2 hover:text-accent hover:underline">
          <MapPin className="h-4 w-4 shrink-0 text-accent" />
          <span className="truncate">{request.destinationName}</span>
        </button>
      </div>

      <div className="flex flex-wrap gap-x-4 gap-y-2 text-xs text-muted-foreground">
        <span className="flex items-center gap-1.5"><CalendarDays className="h-3.5 w-3.5" />{new Date(`${request.date}T00:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</span>
        <span className="flex items-center gap-1.5"><Clock3 className="h-3.5 w-3.5" />{request.startTime}–{request.endTime}</span>
      </div>

      {driverView && request.status === 'open' ? (
        <div className="space-y-2">
          <Button type="button" variant="secondary" className="w-full" onClick={onMessageRider}><MessageSquare className="mr-2 h-4 w-4" />Message rider</Button>
          <div className="grid grid-cols-2 gap-2">
            <Button type="button" disabled={isResponding} className="bg-green-600 text-white hover:bg-green-700" onClick={onAccept}><Check className="mr-1 h-4 w-4" />Accept</Button>
            <Button type="button" disabled={isResponding} variant="outline" className="border-red-200 text-red-600 hover:bg-red-50" onClick={onReject}><X className="mr-1 h-4 w-4" />Reject</Button>
          </div>
        </div>
      ) : driverView && request.status === 'accepted' ? (
        <div className="grid grid-cols-2 gap-2">
          <Button type="button" variant="secondary" onClick={onMessageRider}><MessageSquare className="mr-2 h-4 w-4" />Message rider</Button>
          <Button type="button" variant="outline" disabled={isResponding} className="border-destructive/30 text-destructive hover:bg-destructive/10" onClick={onDelete}><Trash2 className="mr-2 h-4 w-4" />Delete</Button>
        </div>
      ) : !driverView && request.status === 'open' ? (
        <Button type="button" variant="outline" className="w-full border-destructive/30 text-destructive hover:bg-destructive/10" onClick={onDelete}><Trash2 className="mr-2 h-4 w-4" />Delete request</Button>
      ) : null}
    </div>
  </article>
);
